import React, { useEffect, useState } from 'react'
import { doc, onSnapshot } from 'firebase/firestore'
import { db } from '../backend/firebase'
import { useNavigate } from 'react-router-dom';
import ClipLoader from 'react-spinners/ClipLoader';
import { FaDownload, FaChartBar, FaGlobe, FaTag } from 'react-icons/fa';
import { HiOutlineDatabase } from 'react-icons/hi';

const Dataset = ({ datasetid, goNumb = false }) => {
  const [userDetails, setUserDetails] = useState(null);
  const [datasetLoading, setDatasetLoading] = useState(true);
  const [userLoading, setUserLoading] = useState(true);
  const [info, setInfo] = useState(null);
  const navigate = useNavigate();

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatNumber = (num) => {
    if (!num) return '0';
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toString();
  };

  useEffect(() => {
    if (!info?.author) return;
    const userRef = doc(db, "Users", info.author);
    const unsubscribe = onSnapshot(userRef, (doc) => {
      if (doc.exists()) {
        setUserDetails(doc.data());
      }
      setUserLoading(false);
    }, (error) => {
      console.warn(error);
      setUserLoading(false);
    });

    return () => unsubscribe();
  }, [info?.author]);

  useEffect(() => {
    setDatasetLoading(true);
    const datasetRef = doc(db, "datasets", datasetid);
    const unsubscribe = onSnapshot(datasetRef, (data) => {
      if (data.exists()) {
        setInfo(data.data());
      } else {
        setInfo(null);
      }
      setDatasetLoading(false);
    });

    return () => unsubscribe();
  }, [datasetid]);

  if (datasetLoading) {
    return (
      <div style={{ 
        padding: 15, 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center',
        backgroundColor: '#0a0a0a',
        borderRadius: 8,
        margin: '5px 0',
        minHeight: 120
      }}>
        <ClipLoader color="#667eea" size={25} />
      </div>
    );
  }

  if (!info) {
    return (
      <div style={{ 
        padding: 20, 
        display: 'flex',
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center',
        borderRadius: 8,
        margin: '5px 0',
        minHeight: 120
      }}>
        <HiOutlineDatabase size={32} color="#667eea" style={{ marginBottom: 8 }} />
        <span style={{ color: '#e5e7eb', fontSize: 14, fontWeight: 500 }}>Dataset Not Found</span>
      </div>
    );
  }

  return (
    <div
      style={{
        border: '1px solid #1f1f1f',
        padding: '10px',
        borderRadius: '8px',
        margin: '8px 0',
        cursor: goNumb ? 'default' : 'pointer',
        transition: 'all 0.2s ease',
        position: 'relative',
        overflow: 'hidden',
        minHeight: 100,
      }}
      onClick={(goNumb || !info) ? null : () => navigate(`/dataset/${info.uuid}`)}
    >
      {/* Compact Header */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        marginBottom: 12
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>
          {userLoading ? (
            <ClipLoader color="#667eea" size={16} />
          ) : (
            <>
              <img 
                src={userDetails?.profile_pic} 
                style={{ 
                  height: 24, 
                  width: 24, 
                  borderRadius: '50%',
                  border: '1px solid #333'
                }} 
                alt="User"
              />
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <span style={{ fontSize: 12, color: '#f3f4f6', fontWeight: 600 }}>
                  {userDetails?.displayName}
                </span>
                <span style={{ fontSize: 10, color: '#6b7280' }}>
                  {formatDate(info.lastUpdated)}
                </span>
              </div>
            </>
          )}
        </div>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <div style={{
            background: '#667eea',
            color: 'white',
            padding: '5px 10px',
            borderRadius: '4px',
            fontSize: 9,
            fontWeight: 400
          }}>
            {info.visibility}
          </div>
          <div style={{
            background: 'whitesmoke',
            color: '#667eea',
            padding: '5px 10px',
            borderRadius: '4px',
            fontSize: 9,
            fontWeight: 500
          }}>
            v{info.latestVersion}
          </div>
        </div>
      </div>

      {/* Content */}
      <div style={{ marginBottom: 12 }}>
        <h3 style={{ 
          color: '#ffffff', 
          fontSize: 14, 
          fontWeight: 700,
          margin: '0 0 4px 0',
          lineHeight: 1.3
        }}>
          {info.name}
        </h3>
        <p style={{
          color: '#9ca3af',
          fontSize: 11,
          lineHeight: 1.4,
          margin: 0,
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden'
        }}>
          {info.description}
        </p>
      </div>

      {/* Compact Stats */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderTop: '1px solid #1f1f1f',
        paddingTop: 10
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{ fontSize: 11, color: '#e5e7eb', fontWeight: 600 }}>
              {formatNumber(info.fileCount)} files
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{ fontSize: 11, color: '#e5e7eb', fontWeight: 600 }}>
              {formatNumber(info.downloads)} downloads
            </span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span style={{ fontSize: 11, color: '#e5e7eb', fontWeight: 600 }}>
              {formatNumber(info.views)} views
            </span>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {info.tags && info.tags.slice(0, 2).map((tag, index) => (
            <span
              key={index}
              style={{
                background: '#1f1f1f',
                color: '#9ca3af',
                padding: '2px 6px',
                borderRadius: '4px',
                fontSize: 11,
                border: '1px solid #333',
                display: 'flex',
                alignItems: 'center',
                gap: 2
              }}
            >
              {tag}
            </span>
          ))}
          {info.tags && info.tags.length > 2 && (
            <span style={{
              background: '#1f1f1f',
              color: '#667eea',
              padding: '2px 6px',
              borderRadius: '4px',
              fontSize: 11,
              fontWeight: 600,
              border: '1px solid #333',
              display: 'flex',
              alignItems: 'center',
              gap: 2
            }}>
              +{info.tags.length - 2}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dataset;